import { AxiosError } from "axios";

/**
 * Backend AppError structure returned by the API.
 *
 * The `message` field contains a user-friendly error message suitable for display.
 */
export interface AppError {
  errorId: string;
  timestamp: string;
  statusCode: number;
  message: string;
  operation?: string;
  objectType?: string;
  objectId?: string;
  attributes?: Record<string, unknown>;
}

/**
 * Backend error response envelope.
 *
 * Standardized services populate `appError`. Legacy services may only
 * populate the top-level string fields.
 */
export interface BackendErrorResponse {
  appError?: AppError;
  errorMessage?: string;
  ErrorMessage?: string;
  error?: string;
}

const DEFAULT_MESSAGE = "Something went wrong";

const isErrorResponseData = (data: unknown): data is BackendErrorResponse =>
  typeof data === "object" && data !== null;

/**
 * Runtime type guard for backend AppError objects.
 *
 * Some services may return a partial appError payload, so this guard requires
 * a message and validates known field types when present.
 */
export const isAppError = (value: unknown): value is AppError =>
  typeof value === "object" &&
  value !== null &&
  "message" in value &&
  "statusCode" in value &&
  typeof (value as Record<string, unknown>)["message"] === "string";

/**
 * Extracts a user-friendly error message from an API error.
 *
 * Checks fields in priority order:
 * 1. appError.message
 * 2. errorMessage
 * 3. ErrorMessage
 * 4. error (string)
 * 5. Plain string response body
 * 6. Error.message
 * 7. Provided fallback
 */
export const extractErrorMessage = (
  error: unknown,
  fallback: string = DEFAULT_MESSAGE,
): string => {
  if (error instanceof AxiosError && error.response?.data != null) {
    const data: unknown = error.response.data;

    if (typeof data === "string" && data.length > 0) {
      return data;
    }

    if (isErrorResponseData(data)) {
      return (
        data.appError?.message ||
        data.errorMessage ||
        data.ErrorMessage ||
        data.error ||
        fallback
      );
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};
